import permissionsInfo from './permissionsInfo';
import {
  addGroupsToUser,
  addTenantsToUser,
  addTenantsToUserGroup, addTenantUserToGroups,
  removeGroupFromUser,
  removeTenantFromUser, removeTenantUserFromGroup
} from '../api/users';
import {addTenantsToGroup, removeTenantFromGroup} from '../api/groups';
import tenantsRoutes from '../components/tenants/routes';
import groupsRoutes from '../components/group/routes';
import usersRoutes from '../components/user/routes';
import patientsRoutes from '../components/patients/routes';
import i18n from 'i18next';
import ModeEditIcon from '@mui/icons-material/ModeEdit';

export const listData = {
  // system configuration - tenants
  '/system-configuration/tenants': {
    fetchUrl: () => '/tenants',
    header: 'System Configuration - Tenants',
    title: 'List of Tenants',
    buttonLabel: 'Add New Tenant',
    permissions: permissionsInfo.tenants.create,
    loadingText: 'Getting tenants list',
    defaultSort: 'SLUG',
    fields: [{
      name: 'slug',
      label: i18n.t('tenants:tableHeader.slug'),
      link: (id?: string | number) => tenantsRoutes.tenantsItem(id),
      sortName: 'SLUG'
    },
    {
      name: 'name',
      label: i18n.t('tenants:tableHeader.name'),
      sortName: 'NAME'
    },
    {
      name: 'industry',
      label: i18n.t('tenants:tableHeader.industry'),
      sortName: 'INDUSTRY_DESC',
      internal: 'desc'
    },
    {
      name: 'login',
      label: '',
      className: 'cell header-cell narrow-cell',
      link: (id?: string | number) => tenantsRoutes.tenantsItem(id),
      icon: <ModeEditIcon className="icon" />
    }
    ]
  },
  '/system-configuration/tenants/:id/groups': {
    fetchUrl: (id: number) => `/tenants/${id}/groups`,
    header: 'Tenant Groups',
    emptyMessage: (name: string) =>
      `Tenant "${name}" does not currently have any groups`,
    loadingText: 'Getting tenants groups',
    fields: [
      {
        name: 'name',
        label: i18n.t('groups:tableHeader.name'),
        link: (id?: string | number) => groupsRoutes.groupsItem(id)
      }
    ]
  },
  '/system-configuration/tenants/:id/users': {
    fetchUrl: (id: number) => `/tenants/${id}/users`,
    header: 'Tenant Users',
    emptyMessage: (name: string) =>
      `Tenant "${name}" does not currently have any users`,
    loadingText: 'Getting tenant users',
    rowLink: (id) => usersRoutes.usersItem(id),
    rowLinkPermission: permissionsInfo.users.view,
    fields: [
      {
        name: 'firstName',
        label: i18n.t('common:firstName')
      },
      {
        name: 'lastName',
        label: i18n.t('common:lastName')
      },
      {
        name: 'email',
        label: i18n.t('common:email')
      }
    ]
  },

  // system configuration - users
  '/system-configuration/users': {
    fetchUrl: () => '/users',
    header: 'System Configuration - Users',
    title: 'List of Users',
    buttonLabel: 'Add New User',
    permissions: permissionsInfo.users.create,
    loadingText: 'Getting users list',
    defaultSort: 'LAST_NAME',
    fields: [{
      name: 'firstName',
      label: i18n.t('common:firstName'),
      sortName: 'FIRST_NAME'
    },
    {
      name: 'lastName',
      label: i18n.t('common:lastName'),
      sortName: 'LAST_NAME'
    },
    {
      name: 'email',
      label: i18n.t('common:email'),
      sortName: 'EMAIL'
    },
    {
      name: 'edit',
      label: '',
      className: 'cell header-cell narrow-cell'
    }
    ]
  },
  // configuration - users
  '/configuration/users': {
    fetchUrl: () => '/tenants/users',
    header: 'Configuration - Users',
    title: 'List of Users',
    buttonLabel: 'Add New User',
    permissions: permissionsInfo.tenants_config.list.users,
    loadingText: 'Getting current tenant\'s users list',
    defaultSort: 'LAST_NAME',
    fields: [{
      name: 'firstName',
      label: i18n.t('common:firstName'),
      sortName: 'FIRST_NAME'
    },
    {
      name: 'lastName',
      label: i18n.t('common:lastName'),
      sortName: 'LAST_NAME'
    },
    {
      name: 'email',
      label: i18n.t('common:email'),
      sortName: 'EMAIL'
    },
    {
      name: 'invitationAccepted',
      label: i18n.t('user:tableHeader.invitationAccepted')
    },
    {
      name: 'edit',
      label: '',
      className: 'cell header-cell narrow-cell'
    }
    ]
  },
  // patients
  '/patients': {
    fetchUrl: () => '/patients',
    header: 'Patients',
    title: 'List of Patients',
    buttonLabel: 'Add New Patient',
    permissions: permissionsInfo.patients.create,
    loadingText: 'Getting patients list',
    defaultSort: 'SENECA_PATIENT_ID',
    fields: [{
      name: 'id',
      label: 'Seneca Patient ID',
      link: (id?: string | number) => patientsRoutes.item(id),
      sortName: 'SENECA_PATIENT_ID'
    },
    {
      name: 'lastName',
      secondName: 'firstName',
      label: 'Patient Name',
      sortName: 'LAST_NAME,FIRST_NAME'
    },
    {
      name: 'patientId',
      label: 'Patient ID',
      sortName: 'PATIENT_ID'
    }
    ]
  },
  // surveys
  '/surveys': {
    fetchUrl: () => '/surveys',
    buttonLabel: 'Add Survey',
    header: 'Surveys',
    title: 'List of Surveys',
    permissions: permissionsInfo.surveys.create,
    loadingText: 'Getting surveys list',
    defaultSort: 'PATIENT_SURVEY_START_DATE',
    fields: [
      {
        name: 'surveyId',
        label: 'Survey ID',
        sortName: 'PATIENT_SURVEY_ID',
        className: 'cell header-cell id-cell',
        permission: permissionsInfo.surveys.list.view
      },
      {
        name: 'dateCreated',
        label: 'Date Created',
        sortName: 'PATIENT_SURVEY_START_DATE',
        permission: permissionsInfo.surveys.list.view
      },
      {
        name: 'patientName',
        label: 'Patient Name',
        sortName: 'PATIENT_SURVEY_LAST_NAME,PATIENT_SURVEY_FIRST_NAME',
        permission: permissionsInfo.surveys.list.view
      },
      {
        name: 'treatment',
        label: 'Treatment',
        sortName: 'TREATMENT_NAME',
        permission: permissionsInfo.surveys.list.view
      },
      {
        name: 'completed',
        label: 'Completed',
        sortName: 'PATIENT_SURVEY_COMPLETED',
        className: 'cell header-cell id-cell',
        permission: permissionsInfo.surveys.list.view
      },
      {
        name: 'threeDots',
        label: 'Actions',
        className: 'cell header-cell narrow-cell',
        permission: permissionsInfo.surveys.delete
      }
    ]
  },
  '/system-configuration/users/:id/groups': {
    fetchUrl: (id: number) => `/users/${id}/groups`,
    header: 'User Groups',
    emptyMessage: (name: string) =>
      `User "${name}" does not currently have any groups`,
    loadingText: 'Getting user groups',
    add: addGroupsToUser,
    addTenantsToUserGroup: addTenantsToUserGroup,
    remove: removeGroupFromUser,
    spinnerTextRemove: (name: string) =>
      `Removing user from the group "${name}"`,
    spinnerTextAdd: (name: string) =>
      `Adding user "${name}" to the selected groups`,
    spinnerTextAddTenant: (name: string) =>
      `Adding "${name}" group to the selected tenants`,
    addButtonLabel: 'Add to a group',
    popupLabel: (tenantName: string, userName: string) =>
      `User "${userName}" will be removed` +
              ` from the group "${tenantName}"`,
    popupEntityKey: 'list-popup/users/groups',
    isSysConf: (path) => path.includes(usersRoutes.users()),
    permissions: permissionsInfo.users.manage.groups,
    fields: [
      {
        name: 'slug',
        label: i18n.t('groups:tableHeader.slug'),
        link: (id?: string | number) => groupsRoutes.groupsItem(id)
      },
      {
        name: 'name',
        label: i18n.t('groups:tableHeader.name')
      },
      {
        name: 'tenants',
        label: i18n.t('user:tableHeader.tenants')
      }
    ]
  },

  '/configuration/users/:id/groups': {
    fetchUrl: (id: number) => `/tenants/users/${id}/groups`,
    header: 'User Groups',
    emptyMessage: (name: string) =>
      `User "${name}" does not currently have any groups`,
    loadingText: 'Getting user groups',
    add: addTenantUserToGroups,
    addTenantsToUserGroup: addTenantsToUserGroup,
    remove: removeTenantUserFromGroup,
    spinnerTextRemove: (name: string) =>
      `Removing user from the group "${name}"`,
    spinnerTextAdd: (name: string) =>
      `Adding user "${name}" to the selected groups`,
    spinnerTextAddTenant: (name: string) =>
      `Adding "${name}" group to the selected tenants`,
    addButtonLabel: 'Add to a group',
    popupLabel: (tenantName: string, userName: string) =>
      `User "${userName}" will be removed` +
      ` from the group "${tenantName}"`,
    popupEntityKey: 'list-popup/configuration/users/groups',
    isSysConf: (path: string) => path.includes(usersRoutes.users()),
    permissions: permissionsInfo.tenants.manage.groups,
    fields: [
      {
        name: 'slug',
        label: i18n.t('groups:tableHeader.slug')
      },
      {
        name: 'name',
        label: i18n.t('groups:tableHeader.name')
      }
    ]
  },

  '/system-configuration/users/:id/tenants': {
    fetchUrl: (id: number) => `/users/${id}/tenants`,
    header: 'User Tenants',
    emptyMessage: (name: string) =>
      `User "${name}" does not currently have any tenants`,
    loadingText: 'Getting user tenants',
    add: addTenantsToUser,
    remove: removeTenantFromUser,
    spinnerTextRemove: (name: string) =>
      `Removing tenant "${name}" from the user`,
    spinnerTextAdd: (name: string) =>
      `Adding user "${name}" to the selected tenants`,
    addButtonLabel: 'Add to a tenant',
    popupLabel: (tenantName: string, userName: string) =>
      `The tenant "${tenantName}" will be removed` +
              ` from the user "${userName}"`,
    popupEntityKey: 'list-popup/users/tenants',
    fields: [
      {
        name: 'slug',
        label: i18n.t('tenants:tableHeader.slug'),
        link: (id?: string | number) => tenantsRoutes.tenantsItem(id)
      },
      {
        name: 'name',
        label: i18n.t('tenants:tableHeader.name')
      },
      {
        name: 'invitationAccepted',
        label: i18n.t('user:tableHeader.invitationAccepted'),
        valuesMap: {
          true: i18n.t('common:accepted'),
          false: i18n.t('common:invited')
        }
      }
    ]
  },

  // profile groups
  '/profile/groups': {
    fetchUrl: () => `/users/authorized/groups`,
    header: 'User Groups',
    emptyMessage: (name: string) =>
      `User "${name}" does not currently have any groups`,
    loadingText: 'Getting profile groups',
    fields: [{
      name: 'slug',
      label: i18n.t('groups:tableHeader.slug')
    },
    {
      name: 'name',
      label: i18n.t('groups:tableHeader.name')
    }
    ]
  },
  // system configuration - groups
  '/system-configuration/groups': {
    fetchUrl: () => '/groups',
    header: 'System Configuration - Groups',
    title: 'List of Groups',
    buttonLabel: 'Add New Group',
    permissions: permissionsInfo.groups.create,
    loadingText: 'Getting groups list',
    defaultSort: 'SLUG',
    fields: [{
      name: 'slug',
      label: i18n.t('groups:tableHeader.slug'),
      link: (id?: string | number) => groupsRoutes.groupsItem(id),
      sortName: 'SLUG'
    },
    {
      name: 'name',
      label: i18n.t('groups:tableHeader.name'),
      sortName: 'NAME'
    },
    {
      name: 'edit',
      label: '',
      className: 'cell header-cell narrow-cell',
      link: (id?: string | number) => groupsRoutes.groupsItem(id),
      icon: <ModeEditIcon className="icon" />
    }
    ]
  },
  '/system-configuration/groups/:id/users': {
    fetchUrl: (id: number) => `/groups/${id}/users`,
    header: 'Group Users',
    emptyMessage: (name: string) =>
      `Group "${name}" does not currently have any users`,
    loadingText: 'Getting group users',
    rowLink: (id) => usersRoutes.usersItem(id),
    rowLinkPermission: permissionsInfo.users.view,
    fields: [
      {
        name: 'firstName',
        label: i18n.t('common:firstName')
      },
      {
        name: 'lastName',
        label: i18n.t('common:lastName')
      },
      {
        name: 'email',
        label: i18n.t('common:email')
      }
    ]
  },
  '/system-configuration/groups/:id/tenants': {
    fetchUrl: (id: number) => `/groups/${id}/tenants`,
    header: 'Group Tenants',
    emptyMessage: (name: string) =>
      `Group "${name}" does not currently have any tenants`,
    loadingText: 'Getting group tenants',
    add: addTenantsToGroup,
    remove: removeTenantFromGroup,
    spinnerTextRemove: (name: string) =>
      `Removing tenant "${name}" from the group`,
    spinnerTextAdd: (name: string) =>
      `Adding group "${name}" to the selected tenants`,
    addButtonLabel: 'Add to a tenant',
    popupLabel: (tenantName: string, groupName: string) =>
      `The tenant "${tenantName}" will be removed` +
              ` from the group "${groupName}"`,
    popupEntityKey: 'list-popup/groups/tenants',
    fields: [
      {
        name: 'slug',
        label: i18n.t('tenants:tableHeader.slug'),
        link: (id?: string | number) => tenantsRoutes.tenantsItem(id)
      },
      {
        name: 'name',
        label: i18n.t('tenants:tableHeader.name')
      }
    ]
  }
};

export const tenantPopupData = {
  'list-popup/groups/tenants': {
    fetchUrl: () => '/tenants',
    title: 'Select Tenants',
    entityType: 'Group',
    addBtnLabel: ['Tenant', 'Tenants'],
    closeBtnLabel: 'Close',
    permissions: permissionsInfo.groups.create,
    loadingText: 'Getting available tenants list',
    fields: [{
      name: 'slug',
      label: i18n.t('tenants:tableHeader.slug'),
      sortName: 'SLUG',
      link: (id?: string | number) => tenantsRoutes.tenantsItem(id)
    },
    {
      name: 'name',
      label: i18n.t('tenants:tableHeader.name'),
      sortName: 'NAME'
    }
    ]
  },
  'list-popup/users/tenants': {
    fetchUrl: () => '/tenants',
    title: 'Select Tenants',
    entityType: 'User',
    addBtnLabel: ['Tenant', 'Tenants'],
    closeBtnLabel: 'Close',
    permissions: permissionsInfo.users.create,
    loadingText: 'Getting available tenants list',
    fields: [{
      name: 'slug',
      label: i18n.t('tenants:tableHeader.slug'),
      sortName: 'SLUG',
      link: (id?: string | number) => tenantsRoutes.tenantsItem(id)
    },
    {
      name: 'name',
      label: i18n.t('tenants:tableHeader.name'),
      sortName: 'NAME'
    }
    ]
  },
  'list-popup/users/groups': {
    fetchUrl: () => '/groups',
    title: 'Select Groups',
    entityType: 'User',
    addBtnLabel: ['Group', 'Groups'],
    closeBtnLabel: 'Close',
    permissions: permissionsInfo.users.create,
    loadingText: 'Getting available group list',
    fields: [{
      name: 'slug',
      label: i18n.t('groups:tableHeader.slug'),
      sortName: 'SLUG',
      link: (id?: string | number) => groupsRoutes.groupsItem(id)
    },
    {
      name: 'name',
      label: i18n.t('groups:tableHeader.name'),
      sortName: 'NAME'
    }
    ]
  },
  'list-popup/configuration/users/groups': {
    fetchUrl: () => '/tenants/available-groups',
    title: 'Select Groups',
    entityType: 'User',
    addBtnLabel: ['Group', 'Groups'],
    closeBtnLabel: 'Close',
    permissions: permissionsInfo.tenants.manage.groups,
    loadingText: 'Getting available group list',
    fields: [{
      name: 'slug',
      label: i18n.t('groups:tableHeader.slug'),
      sortName: 'SLUG',
      link: (id?: string | number) => groupsRoutes.groupsItem(id)
    },
    {
      name: 'name',
      label: i18n.t('groups:tableHeader.name'),
      sortName: 'NAME'
    }
    ],
    hasSorting: true
  },
  'list-popup/users/groups/available-tenants': {
    fetchUrl: (userId: number, groupId: number) =>
      `users/${userId}/groups/${groupId}/available-tenants`,
    title: 'Select Tenants',
    entityType: 'Group',
    addBtnLabel: ['Update'],
    closeBtnLabel: 'Close',
    permissions: permissionsInfo.users.create,
    loadingText: 'Getting available tenants list',
    fields: [{
      name: 'slug',
      label: i18n.t('tenants:tableHeader.slug'),
      sortName: 'SLUG',
      link: (id?: string | number) => tenantsRoutes.tenantsItem(id)
    },
    {
      name: 'name',
      label: i18n.t('tenants:tableHeader.name'),
      sortName: 'NAME'
    }
    ]
  }
};
